/**
 * Stage 4b: Section Drafting — context-based version using StagePanel.
 *
 * Fan-out stage: drafts each article section individually.
 * Shows section cards with per-item and batch run controls.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { useBatchExecution } from '../shared/useBatchExecution';
import type { ArticleDocument } from '../types';

function SectionCards({ onSelectedIdChange }: { onSelectedIdChange: (id: string | null) => void }) {
  const { document, run } = useArticleContext();
  const sections = document?.article?.sections || [];
  const {
    selectedId, setSelectedId: setSelectedIdInternal,
    runningAll, batchStatus, runningSingle,
    runSingle, runAll, cancelBatch,
  } = useBatchExecution({ stageId: 'stage_4b_section_drafting' });

  const setSelectedId = React.useCallback((id: string | null) => {
    setSelectedIdInternal(id);
    onSelectedIdChange(id);
  }, [setSelectedIdInternal, onSelectedIdChange]);

  const selectedSection = sections.find((s) => s.id === selectedId);

  return (
    <div className="space-y-3">
      {/* Batch controls */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#1a2332' }}
          disabled={runningAll || sections.length === 0}
          onClick={() => runAll(sections.map(s => s.id))}
        >
          {runningAll ? 'Drafting All...' : `Draft All (${sections.length})`}
        </button>
        {runningAll && (
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={cancelBatch}
          >
            Cancel
          </button>
        )}
        <span className="text-xs text-gray-400">
          {sections.filter((s) => s.body).length}/{sections.length} drafted
        </span>
      </div>

      {/* Section cards */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {sections.map((section) => {
          const hasDraft = Boolean(section.body);
          const batch = batchStatus[section.id];
          return (
            <button
              key={section.id}
              type="button"
              className={`flex-shrink-0 border rounded px-3 py-2 text-left text-xs w-44 ${
                selectedId === section.id ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
              } ${hasDraft ? 'bg-green-50' : 'bg-white'}`}
              onClick={() => setSelectedId(section.id)}
            >
              <div className="font-medium text-gray-800 truncate">
                {section.heading || '(Intro)'}
              </div>
              <div className="text-gray-400">{section.heading_level}</div>
              {batch && (
                <div className={`text-[10px] mt-1 ${batch.status === 'succeeded' ? 'text-green-600' : batch.status === 'failed' ? 'text-red-600' : 'text-blue-600'}`}>
                  {batch.status}
                </div>
              )}
              {!batch && hasDraft && (
                <div className="text-[10px] mt-1 text-green-600">drafted</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected section detail */}
      {selectedSection && (
        <div className="border rounded p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-gray-800">
              {selectedSection.heading || '(Intro)'} — {selectedSection.heading_level}
            </div>
            <button
              type="button"
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={runningSingle || runningAll}
              onClick={() => runSingle(selectedSection.id)}
            >
              {runningSingle ? 'Drafting...' : 'Draft Section'}
            </button>
          </div>
          <div className="text-xs text-gray-500 mb-1">{selectedSection.purpose}</div>
          {selectedSection.body ? (
            <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border rounded p-2 max-h-60 overflow-y-auto">
              {selectedSection.body}
            </pre>
          ) : (
            <div className="text-xs text-gray-400">Not yet drafted.</div>
          )}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const sections = doc.article?.sections || [];
  const drafted = sections.filter((s) => s.body);
  if (drafted.length === 0) {
    return <div className="text-sm text-gray-500">No sections drafted yet.</div>;
  }
  const totalWords = drafted.reduce((sum, s) => sum + (s.body?.split(/\s+/).filter(Boolean).length || 0), 0);
  return (
    <div className="text-xs text-gray-600">
      {drafted.length}/{sections.length} sections drafted — ~{totalWords} words total
    </div>
  );
}

export function Stage4bSectionDraftingV2() {
  const { document } = useArticleContext();
  const hasSections = (document?.article?.sections || []).length > 0;
  const [extractionId, setExtractionId] = React.useState<string | null>(null);

  return (
    <StagePanel
      stageId="stage_4b_section_drafting"
      isLlmStage
      title="Stage 4b: Section Drafting"
      description="Drafts each article section individually with research and internal links."
      hideRunButton
      renderOutput={renderOutput}
      extractionId={extractionId || undefined}
    >
      {hasSections ? <SectionCards onSelectedIdChange={setExtractionId} /> : (
        <div className="text-xs text-gray-500">No sections defined. Run Stage 3a first.</div>
      )}
    </StagePanel>
  );
}
